'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useState } from 'react';
/**
 * Hook que retorna true cuando la página ha cargado completamente y el navegador está idle
 * Combina el evento 'load' (todos los recursos terminados) con requestIdleCallback (navegador idle)
 * @param options Configuración del hook
 * @returns true si la página está cargada y el navegador está idle
 */
export var usePageIdle = function usePageIdle() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$timeout = options.timeout,
    timeout = _options$timeout === void 0 ? 2000 : _options$timeout,
    _options$fallbackTime = options.fallbackTimeout,
    fallbackTimeout = _options$fallbackTime === void 0 ? 100 : _options$fallbackTime;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPageIdle = _useState2[0],
    setIsPageIdle = _useState2[1];
  useEffect(function () {
    var isIdle = false;
    var idleId = null;
    var timeoutId = null;
    var checkIfPageIsIdle = function checkIfPageIsIdle() {
      // Solo proceder si la página está completamente cargada
      if (document.readyState !== 'complete') {
        return;
      }

      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (isIdle) return; // Prevenir múltiples llamadas
      isIdle = true;

      // Usar requestIdleCallback para esperar el tiempo idle del navegador
      if (window.requestIdleCallback) {
        idleId = window.requestIdleCallback(function () {
          setIsPageIdle(true);
        }, {
          timeout: timeout
        } // Fallback después del tiempo especificado
        );
      } else {
        // Fallback para navegadores sin requestIdleCallback (Safari)
        timeoutId = setTimeout(function () {
          setIsPageIdle(true);
        }, fallbackTimeout);
      }
    };

    // Si la página ya está cargada, verificar inmediatamente
    if (document.readyState === 'complete') {
      checkIfPageIsIdle();
    } else {
      // Esperar el evento load (todos los recursos terminaron de cargar)
      window.addEventListener('load', checkIfPageIsIdle);
    }
    return function () {
      window.removeEventListener('load', checkIfPageIsIdle);
      if (idleId && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeout, fallbackTimeout]);
  return isPageIdle;
};

/**
 * Hook alternativo que solo verifica si la página está completamente cargada (sin idle callback)
 * @returns true si la página está completamente cargada
 */
export var usePageLoaded = function usePageLoaded() {
  var _useState3 = useState(document.readyState === 'complete'),
    _useState4 = _slicedToArray(_useState3, 2),
    isPageLoaded = _useState4[0],
    setIsPageLoaded = _useState4[1];
  useEffect(function () {
    var handleLoad = function handleLoad() {
      setIsPageLoaded(true);
    };
    if (document.readyState === 'complete') {
      setIsPageLoaded(true);
    } else {
      window.addEventListener('load', handleLoad);
      return function () {
        return window.removeEventListener('load', handleLoad);
      };
    }
  }, []);
  return isPageLoaded;
};

// Exportar usePageIdle como default para compatibilidad
export default usePageIdle;